import type { LucideIcon } from "lucide-react";
import { Megaphone, Rocket, UsersRound, Zap } from "lucide-react";

/**
 * The XO "Getting Started" launch journey.
 *
 * Four phases, fifteen steps. Copy and structure follow the design prototype
 * (Getting Started Flow, Sep 30) — see docs/prototypes/getting-started-flow.html.
 *
 * THE SERVER OWNS THE IDS, THIS FILE OWNS THE WORDS. Completion is persisted
 * per task key by the backend (`getting_started_completions`), which validates
 * every key against `GETTING_STARTED_TASK_KEYS` in app/core/constants.py.
 * Everything else — titles, guidance, ordering, phase grouping, the unlock
 * rules — lives here, so a wording change ships without a migration. The
 * consequence is that the key union below and that frozenset must agree.
 *
 * KEEP `GettingStartedTaskKey` IN SYNC with GETTING_STARTED_TASK_KEYS server-side.
 *
 * The one exception is `xo.activate.conduct`, the step that took over the XO
 * Home checklist (sign the Code of Conduct, read the two onboarding PDFs). Its
 * state already lives on the user — `code_of_conduct_accepted` and the FTUX
 * step bitmask — so it is derived from that, never stored here, and its key is
 * never sent to the getting-started endpoints. See `mandatory` below.
 */

export type GettingStartedTaskKey =
  // Phase 1 — Activate
  | "xo.activate.profile"
  | "xo.activate.conduct"
  | "xo.activate.linkedin"
  | "xo.activate.stars"
  | "xo.activate.calibration"
  | "xo.activate.kickoff"
  // Phase 2 — Publish
  | "xo.publish.live"
  | "xo.publish.carousel"
  | "xo.publish.announce"
  // Phase 3 — Connect
  | "xo.connect.peer"
  | "xo.connect.refer-achievers"
  | "xo.connect.refer-xos"
  // Phase 4 — Launch
  | "xo.launch.design";

/**
 * `xo.connect.opportunities` ("Explore capability-flag opportunities") is
 * deliberately absent. It is in the design but out of the flow until
 * capability-flag routing exists; adding it back is this union, the phase
 * below, and the server's frozenset — no migration.
 */

/**
 * How a step's tick is decided — and therefore whether it needs a stored row.
 *
 *  - `acknowledged`: the user says it happened. The platform cannot observe
 *    the work, so the claim IS the record and has to be persisted.
 *  - `derived`: read from data the platform already holds. Nothing is stored
 *    and the tick cannot drift, because it is computed from the truth.
 */
export type CompletionSource = "acknowledged" | "derived";

export type GettingStartedAction =
  | { kind: "navigate"; to: string }
  | { kind: "external"; url: string }
  /** Opens an in-page modal rather than going anywhere. */
  | { kind: "modal"; modal: "calibration" | "carousel" | "referAchievers" | "referXos" };

/** The onboarding PDFs — the same files the XO Home checklist linked. */
export const FTUX_PDF_URLS = {
  advisorExplainer: "https://fsv-public-assets.s3.us-east-2.amazonaws.com/fsv_explainer.pdf",
  achieverArchetypes: "https://fsv-public-assets.s3.us-east-2.amazonaws.com/fsv_archetypes.pdf",
} as const;

/**
 * An ordered block of step guidance.
 *
 * The previous shape was a fixed `description` + `sections[]`, which could not
 * express the design's interleaving of prose, headed lists, a copyable AI
 * prompt and S.T.A.R. cards. Blocks render in array order, so the copy reads
 * as written instead of being reflowed into a fixed skeleton.
 */
export type GettingStartedBlock =
  | { kind: "p"; text: string }
  /** A section label. `spacing` follows the Figma gaps: md 28/10, sm 15/10. */
  | { kind: "h"; text: string; spacing?: "md" | "sm" }
  /**
   * Rendered as the design's guided grid: an emblem, a short bold lead and the
   * item as its explanation. The lead is the item's own "Lead: text" prefix
   * when it has one, else `labels[i]`; with neither, the item stays a bullet.
   * `numbered` swaps the emblems for 01, 02…; `tips` keeps a plain list.
   */
  | {
      kind: "ul";
      items: string[];
      labels?: string[];
      variant?: "grid" | "numbered" | "tips";
    }
  /** Copyable suggested wording, with a "Copy wording" button. */
  | { kind: "quote"; label: string; text: string }
  /** Side-by-side "what counts" / "not enough" examples. */
  | {
      kind: "contrast";
      goodLabel?: string;
      badLabel?: string;
      good: string[];
      bad: string[];
      note?: string;
    }
  /** A one-line session summary card (e.g. the calibration call). */
  | { kind: "session"; title: string; meta: string }
  /** Tinted cards led by a large initial — the S.T.A.R. structure. */
  | { kind: "letterCards"; items: { word: string; text: string }[] }
  /**
   * Story cards as designed (Figma 338:4207): tag, title, a large before →
   * after figure, its caption and one supporting line.
   */
  | {
      kind: "storyCards";
      items: { tag: string; title: string; result: string }[];
    }
  /** A ruled one-line note: a bold label beside its text ("Before you submit"). */
  | { kind: "note"; label: string; text: string }
  /**
   * Monospace, copyable. Tokens are filled from the picked S.T.A.R.: `@@STAR@@`
   * with its summary, and every token in `PROGRAM_COACH_PROMPT_TOKENS` with
   * the AI-generated value once the user asks for one, else its default.
   */
  | { kind: "prompt"; text: string }
  /** The user's S.T.A.R. portfolio, read-only. */
  | { kind: "stars" }
  /** Selectable S.T.A.R. cards; the choice feeds the `prompt` block. */
  | { kind: "starPicker" }
  /**
   * The expert onboarding checklist that used to sit on the XO Home page:
   * Code of Conduct sign-off plus the two onboarding PDFs, each a row with its
   * own action (OnboardingChecklist).
   */
  | { kind: "onboardingChecklist" };

export interface GettingStartedTask {
  key: GettingStartedTaskKey;
  title: string;
  /** One line on why it matters — shown collapsed. */
  why: string;
  /**
   * Rough time to do it, in minutes.
   *
   * Carried as data but NOT rendered, matching the design — which also holds
   * a per-step `mins` and shows only the per-phase roll-up on the welcome
   * card. A badge on every step header turned the list into a price list.
   */
  mins: number;
  /** Full guidance, shown on expand. */
  content: GettingStartedBlock[];
  cta: string;
  source: CompletionSource;
  /**
   * An optional step does not block its phase from completing. Only
   * `xo.connect.peer` is optional today.
   */
  optional?: boolean;
  /**
   * Shows the "Already done? Mark complete" link while the step is not done,
   * for work the expert may have finished elsewhere in the app. Offered on
   * the S.T.A.R. and calibration steps, Publish 2–3 and every Connect step.
   */
  allowMarkComplete?: boolean;
  /**
   * Required onboarding that can only be completed by doing it — the Code of
   * Conduct step, done once all three of its checklist rows are. No manual
   * tick, no "Already done? Mark complete", no Undo: the tick comes from the
   * user's own record, so a hand-tick would be a promise the data can't keep.
   */
  mandatory?: boolean;
  /**
   * Renders the inline referral box — progress dots, the names so far, and the
   * button that opens the modal. Set on the two Phase 3 referral steps.
   */
  referrals?: "achiever" | "xo";
  /**
   * Whether the CTA button goes somewhere real. A step with no action is still
   * fully tickable by hand — the design has no "locked step" concept, only
   * locked phases.
   */
  action?: GettingStartedAction;
  /** The step's side card in the design — what doing it unlocks, in brief. */
  hint: GettingStartedHint;
  /** The glyph after the CTA label; "→" unless the design says otherwise. */
  ctaGlyph?: string;
  /**
   * `column` keeps the action row under the guidance only, beside the hint
   * card rather than across the full card (Figma Phase 1 · Step 3).
   */
  ctaPlacement?: "column" | "hint" | "session";
  /**
   * How many trailing `h`-led sections render in the right column, under the
   * hint card, instead of in the guidance. Stacked one per row there.
   */
  asideSections?: number;
  /** Render the step's `note` block at the top of the right column. */
  asideNote?: boolean;
}

export interface GettingStartedHint {
  label: string;
  title: string;
  text: string;
  footer: string;
  /**
   * The side card's two Figma layouts: `count` (Phase 1 · Step 1) shows the
   * big "01 of 05"; `icon` (the default, Phase 1 · Step 2) shows a glyph at
   * the top and sets the footer off with a purple rule.
   */
  variant?: "count" | "icon" | "panel";
  glyph?: string;
  /** The `panel` layout's icon (Figma 338:4207). */
  icon?: LucideIcon;
}

export interface GettingStartedPhase {
  id: number;
  name: string;
  /** The headline shown beside "Phase N". */
  title: string;
  tagline: string;
  /** Rendered in the rail node. A component reference, so this file stays
   *  plain data and needs no JSX. */
  icon: LucideIcon;
  /** Rough total, for the welcome card. */
  estLabel: string;
  tasks: GettingStartedTask[];
}

const ACTIVATE_TASKS: GettingStartedTask[] = [
  {
    key: "xo.activate.profile",
    title: "Complete your AchieveOS profile.",
    why: "Functional archetype, engagement preferences and five capability flags.",
    mins: 10,
    cta: "Update profile",
    allowMarkComplete: true,
    action: { kind: "navigate", to: "/profile" },
    source: "acknowledged",
    // Copy follows Figma 338:2711 (Experts > Get Started > Phase 1 > Step 1).
    content: [
      {
        kind: "p",
        text: "Your profile powers matching, Personal Advisory Board invitations, program recommendations and your public directory listing. Give it an unhurried thirty minutes rather than a rushed five.",
      },
      { kind: "h", text: "Five things to make specific" },
      {
        kind: "ul",
        items: [
          "One lane: Operator, Builder, Commercial, Technical or Advisor. Choose what people already come to you for.",
          "PAB seats, 1:1 coaching, workshops, office hours, fractional. Select only what you would accept next month.",
          "Up to five problems you solve: pricing architecture, first commercial hires, org design, Series A narrative, platform reliability.",
          "So invitations arrive with realistic dates attached, not generic availability requests.",
          "Plain language. No frameworks, no adjectives you wouldn't say aloud.",
        ],
        labels: [
          "Your functional archetype",
          "Engagement preferences",
          "Capability flags",
          "Availability + time zone",
          "Your two- to three-line bio",
        ],
      },
    ],
    hint: {
      label: "What this unlocks",
      title: "Be known for the right work.",
      text: "A clear role helps the right people understand what to call you for — before your spotlight goes live.",
      footer: "Start with the expertise that others already seek you out for.",
    },
  },
  {
    key: "xo.activate.conduct",
    title: "Code of Conduct",
    why: "Sign off the Code of Conduct and read the two onboarding guides — all three are required.",
    mins: 15,
    cta: "",
    source: "derived",
    mandatory: true,
    content: [
      {
        kind: "p",
        text: "Three things every Expert does before working with Achievers. Signing the Code of Conduct is mandatory — matching, Personal Advisory Board invitations and program co-advising all stay closed until you do. The two guides set expectations for your first sessions.",
      },
      { kind: "onboardingChecklist" },
    ],
    hint: {
      label: "Required before matching",
      title: "Start on the same page.",
      text: "Signing the Code of Conduct opens matching, PAB invitations and program co-advising.",
      footer: "This step ticks itself once all three items are done.",
    },
  },
  {
    key: "xo.activate.linkedin",
    title: "Reflect your Expert Operator role on LinkedIn",
    why: "Candidates check LinkedIn within 24h of an invitation.",
    mins: 5,
    cta: "Update on LinkedIn",
    ctaGlyph: "✓",
    action: {
      kind: "external",
      url: "https://www.linkedin.com/in/me/edit/forms/position/new/",
    },
    source: "acknowledged",
    allowMarkComplete: true,
    // Copy follows Figma 338:3102 (Experts > Get Started > Phase 1 > Step 2).
    content: [
      {
        kind: "p",
        text: "Founding Experts are the public face of the network. When your LinkedIn says it plainly, referrals, achievers and other experts can place you immediately — and your spotlight carousel in Week 2 lands with context instead of cold.",
      },
      { kind: "h", text: "What to change" },
      {
        kind: "ul",
        variant: "numbered",
        items: [
          'Add "Founding Expert, Forward Share Ventures" after your primary title.',
          "Add Forward Share Ventures as a current role — Founding Expert — with the month you were approved.",
          "One sentence on the kind of work you take on through FSV and who it is for.",
          "Leave a slot free — your spotlight carousel goes there in Week 2.",
        ],
        labels: ["Headline", "Experience", "About", "Featured"],
      },
      { kind: "h", text: "Suggested wording" },
      {
        kind: "quote",
        label: "Suggested wording / Adapt in your own voice",
        text: "Founding Expert at Forward Share Ventures — advising founders and operating teams on [your capability flags], through peer advisory boards and short, outcome-shaped programs.",
      },
    ],
    hint: {
      label: "The public signal",
      title: "Let people place you.",
      text: "A clear role helps the right people understand what to call you for—before your spotlight goes live.",
      footer: '"Founding Expert" should sit alongside your primary identity, not replace it.',
      glyph: "↗",
    },
  },
  {
    key: "xo.activate.stars",
    title: "Capture three S.T.A.R. stories.",
    why: "The evidence behind your capability flags — and your matching summaries.",
    mins: 20,
    cta: "Add another S.T.A.R.",
    // The prototype points at /stars, which in this app is the achiever's
    // OKR-achievement page. An XO's portfolio stories live on their profile.
    action: { kind: "navigate", to: "/profile?tab=star-portfolio#star-portfolio" },
    source: "derived",
    allowMarkComplete: true,
    // Layout follows Figma 338:4207 (Experts > Get Started > Phase 1 > Step 3).
    content: [
      {
        kind: "p",
        text: "These stories power your public summary, spotlight carousel and the mini program AI Coach proposes in Phase 4. One properly documented case matters more than a collection of vague claims.",
      },
      { kind: "h", text: "S.T.A.R. / The four-part structure", spacing: "md" },
      {
        kind: "letterCards",
        items: [
          { word: "Situation", text: "The company, stage and state of play when you arrived." },
          { word: "Task", text: "The decision or outcome you were accountable for." },
          { word: "Action", text: "What you did in sequence—and chose not to do." },
          { word: "Result", text: "Three to five quantified outcomes and a time frame." },
        ],
      },
      {
        kind: "contrast",
        goodLabel: "Specific / what counts",
        badLabel: "Too vague / needs evidence",
        good: [
          '"Cut onboarding time from 21 days to 6 in one quarter."',
          '"Raised NRR from 96% to 112% over three quarters."',
        ],
        bad: ['"Improved onboarding."', '"Helped the team ship faster."'],
      },
      {
        kind: "note",
        label: "Before you submit",
        text: "Write in first person and past tense. Name the constraint you worked under. Get permission for anything identifying. If numbers are confidential, use ratios or percentage changes.",
      },
    ],
    hint: {
      label: "The evidence engine",
      title: "A good story has receipts.",
      text: "Keep the measurable outcome, time frame and your actual decisions together. That's what builds credibility.",
      footer:
        "Your existing S.T.A.R.s are ready to review—not automatically approved for publishing.",
    },
  },
  {
    key: "xo.activate.calibration",
    title: "1:1 calibration with the Network team",
    why: "A 30-min review to sharpen your flags before you go live.",
    mins: 3,
    cta: "Pick a time",
    action: { kind: "modal", modal: "calibration" },
    source: "acknowledged",
    content: [
      {
        kind: "p",
        text: "A thirty-minute recorded conversation with the Network team. It is not an assessment — it is raw material. The cut is used on your public profile, in your launch bundle and as short clips alongside your spotlight carousel.",
      },
      { kind: "h", text: "How it runs" },
      {
        kind: "ul",
        items: [
          "30 minutes on Zoom, recorded. Camera on, no slides.",
          "Roughly: how you got here, the S.T.A.R. in your own words, what you will not take on, and who should call you.",
          "You get the cut to review before anything is published.",
        ],
        labels: ["The format", "The conversation", "Review before publishing"],
      },
      { kind: "h", text: "How to prepare" },
      {
        kind: "ul",
        items: [
          "Submit your S.T.A.R.s first — the conversation is built on it.",
          "Have one number you are proud of and one thing that went badly. The second is what makes people trust the first.",
          "Sit somewhere quiet with a window in front of you, not behind.",
        ],
        labels: [
          "Submit your S.T.A.R.s",
          "Bring one win and one setback",
          "Choose your recording space",
        ],
      },
      {
        kind: "session",
        title: "Calibration with the Network team",
        meta: "30 min · Zoom, recorded",
      },
    ],
    hint: {
      label: "A human conversation",
      title: "Let your story speak.",
      text: "Bring a S.T.A.R., an honest constraint and the problem you want to be called about.",
      footer: "You get to review the cut before anything is published.",
    },
  },
  {
    key: "xo.activate.kickoff",
    title: "Attend cohort kick-off",
    why: "Meet the founding cohort and learn how the network operates.",
    mins: 2,
    cta: "Pick a kick-off session",
    // ?q= pre-applies the Events page's title filter, so the expert lands on
    // the kick-off sessions rather than the whole calendar. The term must
    // match the seeded event titles — see
    // venture-pabs-backend/scripts/seed_cohort_kickoff_events.py.
    action: { kind: "navigate", to: "/events?q=Founding%20Cohort%2001%20kick-off" },
    source: "acknowledged",
    content: [
      {
        kind: "p",
        text: "One hour, live, with the Network team and the ten other experts in Founding Cohort 01. This is where the four weeks are set out and where the cohort becomes a group of people rather than a list of names.",
      },
      { kind: "h", text: "Agenda" },
      {
        kind: "ul",
        items: [
          "How ForwardShare works end to end — achievers, PABs, programs and where experts sit.",
          "The four phases, what is automated and what needs you.",
          "Two-minute round of introductions — your lane and the one problem you most want to be called about.",
          "Open questions. Nothing is off the table.",
        ],
        labels: [
          "The ForwardShare system",
          "How the four phases work",
          "Meet the other experts",
          "Anything you want to ask",
        ],
      },
      { kind: "h", text: "Come with" },
      {
        kind: "ul",
        items: [
          'Your one-line answer to "who should call you".',
          "One thing you want out of the four weeks that is not a logo on your profile.",
        ],
        labels: ["Your one-line introduction", "Your personal goal"],
      },
    ],
    hint: {
      label: "Founding Cohort 01",
      title: "Find your people.",
      text: "Meet the other experts, share your lane and learn how the system works.",
      footer: "Hosted live by the Network team.",
    },
  },
];

const PUBLISH_TASKS: GettingStartedTask[] = [
  {
    key: "xo.publish.live",
    title: "Public profile auto-publishes to the directory",
    why: "First name + last initial with an AI-generated summary, once your S.T.A.R.s and profile are approved.",
    mins: 5,
    cta: "Preview profile",
    // ?preview=me opens the Directory with this user's own profile drawer
    // already showing. The step is "review what the network will see", so
    // landing on a grid of other people and making them hunt for themselves
    // would be the wrong half of the job.
    action: { kind: "navigate", to: "/directory?preview=me" },
    source: "acknowledged",
    allowMarkComplete: true,
    content: [
      {
        kind: "p",
        text: "Once your profile and S.T.A.R. are approved, your public listing is generated and published to the Achieve directory automatically. Nothing goes live before you have seen it.",
      },
      { kind: "h", text: "What is published" },
      {
        kind: "ul",
        items: [
          "First name and last initial — full names are shown only to achievers you are matched with.",
          "An AI-written summary drawn from your profile and S.T.A.R., in the network's voice.",
          "Functional archetype, capability flags, engagement preferences and location.",
          "Your photo, and a clip from the video interview once the cut is approved.",
        ],
        labels: [
          "Your directory identity",
          "Your AI-written summary",
          "Expertise and preferences",
          "Photo and approved video",
        ],
      },
      { kind: "h", text: "What stays private" },
      {
        kind: "ul",
        items: [
          "Client names, contract values and anything you marked confidential in the S.T.A.R.",
          "Your calendar, rates and contact details — introductions are always routed through Achieve.",
        ],
        labels: ["Confidential client details", "Personal and commercial information"],
      },
    ],
    hint: {
      label: "Your public presence",
      title: "Show the right version of you.",
      text: "Your listing introduces your strengths without giving away confidential client information.",
      footer: "Review your summary and capability flags before approval.",
    },
  },
  {
    key: "xo.publish.carousel",
    title: "Approve your LinkedIn spotlight carousel",
    why: "Our team turns your S.T.A.R.s into a carousel PDF — preview it, download it, approve it.",
    mins: 10,
    cta: "Review & approve",
    action: { kind: "modal", modal: "carousel" },
    source: "acknowledged",
    allowMarkComplete: true,
    content: [
      {
        kind: "p",
        text: "Five slides, drafted from your S.T.A.R.s, in FSV's visual template. You edit the words; the layout is handled. Most experts need one pass of tightening, not a rewrite.",
      },
      { kind: "h", text: "The five slides" },
      {
        kind: "ul",
        variant: "numbered",
        items: [
          "The sentence that makes an operator stop scrolling. Usually the uncomfortable version of the problem.",
          "What was actually at risk — money, time, a team, a raise.",
          "The call you made. One decision, not a framework.",
          "Your strongest quantified result with a time frame.",
          "The transferable line. What you would tell someone facing the same week.",
        ],
        labels: ["Hook", "Stakes", "Move", "Outcome", "Lesson"],
      },
    ],
    hint: {
      label: "Your public signal",
      title: "One strong story. Five slides.",
      text: "Turn one documented result into a LinkedIn carousel you would actually say aloud.",
      footer: "Review the words before any publishing decision.",
    },
  },
  {
    key: "xo.publish.announce",
    title: "Post the launch announcement",
    why: "Publish the announcement paired with your carousel.",
    mins: 5,
    cta: "Post announcement",
    action: { kind: "external", url: "https://www.linkedin.com/feed/?shareActive=true" },
    source: "acknowledged",
    allowMarkComplete: true,
    content: [
      {
        kind: "p",
        text: "Your carousel plus a short announcement that you have joined as a Founding Expert. This is the post that turns your profile from live into known.",
      },
      { kind: "h", text: "Caption shape" },
      {
        kind: "ul",
        variant: "numbered",
        items: [
          "Line 1: what you are now doing and with whom.",
          "Lines 2–3: the problem you take on, in your clients' words.",
          "Line 4: a plain invitation — who should get in touch and about what.",
          "Tag: Forward Share Ventures, so it lands in the network feed too.",
        ],
      },
      { kind: "h", text: "Timing and follow-through" },
      {
        kind: "ul",
        items: [
          "Tuesday to Thursday morning in your own time zone performs best.",
          "Answer every comment for the first 48 hours — reach follows replies.",
          "Pin the post and set the carousel as a Featured item.",
        ],
        labels: [
          "Choose a posting window",
          "Stay in the conversation",
          "Make your post easy to find",
        ],
      },
    ],
    hint: {
      label: "Go public",
      title: "Let the right people know.",
      text: "Tell people who you serve and what to contact you about. Keep it direct.",
      footer: "Pin the announcement and set the carousel as Featured.",
    },
  },
];

const CONNECT_TASKS: GettingStartedTask[] = [
  {
    key: "xo.connect.peer",
    title: "1:1 with a cohort peer",
    why: "Optional, but the highest-signal half hour of the month.",
    mins: 5,
    optional: true,
    cta: "Book Peer 1:1",
    // The Directory is the experts page. Open a peer's profile there and
    // "Request Intro" books a 30-minute 1:1 against their own calendar,
    // the same machinery the calibration step uses.
    action: { kind: "navigate", to: "/directory" },
    source: "acknowledged",
    allowMarkComplete: true,
    content: [
      {
        kind: "p",
        text: "Optional, and the step returning experts say they would repeat first. Half an hour with someone from your cohort who is solving a different version of the same problem.",
      },
      { kind: "h", text: "How to pick" },
      {
        kind: "ul",
        items: [
          "Not your mirror image. Overlapping stage, different function, is the sweet spot.",
          "Someone whose S.T.A.R. you had a question about while reading it.",
        ],
        labels: ["A different perspective", "A real question to ask"],
      },
      { kind: "h", text: "A 30-minute agenda that works" },
      {
        kind: "ul",
        variant: "numbered",
        items: [
          "5 min: how you each ended up on the advisory path.",
          "10 min each: one live problem, no preamble, other person only asks questions.",
          "5 min: one introduction or resource you can each make good on this week.",
        ],
      },
    ],
    hint: {
      label: "A better connection",
      title: "Find another perspective.",
      text: "Overlapping stage, different function: a useful 30-minute conversation can open a door.",
      footer: "This step is optional — skip it if the timing is not right.",
    },
  },
  {
    key: "xo.connect.refer-achievers",
    title: "Refer three achievers",
    why: "Founders and operators who would get real value from a PAB.",
    mins: 5,
    cta: "Refer an achiever",
    action: { kind: "modal", modal: "referAchievers" },
    referrals: "achiever",
    source: "acknowledged",
    allowMarkComplete: true,
    content: [
      {
        kind: "p",
        text: "Achievers are the people Founding Experts end up advising. Three referrals is the ask — people you would genuinely want on the other side of the table.",
      },
      { kind: "h", text: "Who is a good achiever referral" },
      {
        kind: "ul",
        items: [
          "A founder or senior operator with a goal they are already stuck on, not a vague ambition.",
          "Someone who has paid for advice before and got value from it.",
          "Enough runway or budget to commit to a few months of cadence.",
        ],
        labels: [
          "A real, immediate goal",
          "Prior experience with advice",
          "The resources to participate",
        ],
      },
      { kind: "h", text: "What happens next" },
      {
        kind: "ul",
        items: [
          "They receive a warm introduction naming you, never a cold blast.",
          "Achievers are matched within a week.",
        ],
        labels: ["A warm introduction", "Matched within a week"],
      },
    ],
    hint: {
      label: "Quality over volume",
      title: "Refer someone you believe in.",
      text: "A founder or senior operator with a real goal and the readiness to work on it.",
      footer: "Each person receives a warm introduction naming you.",
    },
  },
  {
    key: "xo.connect.refer-xos",
    title: "Refer three Expert Operators",
    why: "Operators who would make strong experts in the next cohort.",
    mins: 5,
    cta: "Refer an Expert Operator",
    action: { kind: "modal", modal: "referXos" },
    referrals: "xo",
    source: "acknowledged",
    allowMarkComplete: true,
    content: [
      {
        kind: "p",
        text: "The network grows on the judgement of its Founding Experts. Three expert referrals seed the next cohort.",
      },
      { kind: "h", text: "Who is a good Expert Operator referral" },
      {
        kind: "ul",
        items: [
          "Ten years plus of operating depth in one lane, with outcomes they can quantify.",
          "Already advising informally and wondering how to make it a practice.",
          "Someone you would put in front of your own best client.",
        ],
        labels: ["Deep operating experience", "Readiness to advise", "Someone you would endorse"],
      },
      { kind: "h", text: "What happens next" },
      {
        kind: "ul",
        items: [
          "They receive a warm introduction naming you.",
          "They enter the next cohort's review.",
        ],
        labels: ["A named introduction", "Review for the next cohort"],
      },
    ],
    hint: {
      label: "Grow the expert network",
      title: "Your judgement matters.",
      text: "Invite an operator whose real-world work you would trust in front of your best client.",
      footer: "Experts enter the next cohort's review.",
    },
  },
];

/**
 * The Program Coach prompt's blanks, the API field that fills each, and what
 * shows until the user generates one. `@@STAR@@` is the "why me" line: a
 * placeholder until a S.T.A.R. is chosen, then the AI's credibility claim.
 *
 * The AI's values are slotted in after "The problem I want to solve:" etc., so
 * the backend instructs it to match these fallbacks' shape — lowercase noun
 * phrases, no trailing period. Change one side and check the other
 * (venture-pabs-backend app/services/program_coach_prompt.py).
 */
export const PROGRAM_COACH_PROMPT_TOKENS = [
  {
    token: "@@PROBLEM@@",
    field: "problem",
    fallback: "[e.g. usage-based pricing that is leaking revenue]",
  },
  {
    token: "@@AUDIENCE@@",
    field: "audience",
    fallback: "[e.g. seed-to-Series-A B2B founders with a first commercial hire]",
  },
  { token: "@@STAR@@", field: "whyMe", fallback: null },
  {
    token: "@@COMMITMENT@@",
    field: "weeklyCommitment",
    fallback: "[e.g. one 60-min live session plus async review]",
  },
] as const;

const LAUNCH_TASKS: GettingStartedTask[] = [
  {
    key: "xo.launch.design",
    title: "Design your mini program with the AI Program Coach",
    why: "Start from your own idea, or let the Coach propose concepts from your S.T.A.R. Portfolio.",
    mins: 15,
    cta: "Create your first program",
    // Same place the Programs page's "Create New Program" button goes.
    action: { kind: "navigate", to: "/programs/new" },
    source: "acknowledged",
    allowMarkComplete: true,
    content: [
      {
        kind: "p",
        text: "The Program Coach turns your experience into an enrollable mini program. You can hand it a starting point of your own choosing — a topic, a client pattern, a problem you are tired of seeing — and it will shape it into a title, a buyer and an outcome promise.",
      },
      { kind: "h", text: "Pick the S.T.A.R. to build on" },
      {
        kind: "p",
        text: "Pick one of your S.T.A.R.s, then press Generate prompt — the AI drafts your starting point from it below. Each generation uses one of your daily AI credits, so you choose how many to try. Your pick and its prompt are kept for when you come back.",
      },
      { kind: "starPicker" },
      { kind: "h", text: "Your prompt" },
      {
        kind: "prompt",
        text: `You are my Program Coach. Help me design a four-week mini program I can run for a small cohort.

Here is my starting point:
• The problem I want to solve: @@PROBLEM@@
• Who I want to serve: @@AUDIENCE@@
• Why me: @@STAR@@
• What I can commit weekly: @@COMMITMENT@@

Give me three concepts. For each, return:
1. A title an operator would click.
2. The buyer in one sentence — role, stage, and the trigger that makes this urgent now.
3. An outcome promise stated as a measurable change in four weeks.
4. The four weekly milestones that get them there.
5. What I will explicitly NOT cover, so the scope holds.

Then tell me which concept is strongest against my S.T.A.R. evidence, and what proof I am missing to make the promise credible.`,
      },
    ],
    hint: {
      label: "Build from your proof",
      title: "One buyer. One outcome. Four weeks.",
      text: "Your best program starts with a S.T.A.R. you can substantiate, not a topic that sounds broad.",
      footer: "The Coach is better at sharpening a real idea than inventing one.",
    },
  },
];

export const GETTING_STARTED_PHASES: GettingStartedPhase[] = [
  {
    id: 1,
    name: "Activate",
    title: "Set up your presence",
    tagline: "Get your profile and story ready so matching can find you.",
    icon: Zap,
    estLabel: "~55 min",
    tasks: ACTIVATE_TASKS,
  },
  {
    id: 2,
    name: "Publish",
    title: "Go public",
    tagline: "Your listing and spotlight go live — drafted for you, approved by you.",
    icon: Megaphone,
    estLabel: "~20 min",
    tasks: PUBLISH_TASKS,
  },
  {
    id: 3,
    name: "Connect",
    title: "Work the network",
    tagline: "Meet a peer and refer three achievers and three Expert Operators.",
    icon: UsersRound,
    estLabel: "~15 min",
    tasks: CONNECT_TASKS,
  },
  {
    id: 4,
    name: "Launch",
    title: "Launch your first program",
    tagline: "Design your first mini program with the Coach and send it for review.",
    icon: Rocket,
    estLabel: "~15 min",
    tasks: LAUNCH_TASKS,
  },
];

/**
 * The ask, and the bar — deliberately different numbers.
 *
 * The step says "Refer three", and the progress dots count to three, because
 * three is what the programme wants. But ONE is enough to tick it: referring
 * is a favour, not a gate, and plenty of experts will have made the
 * introduction offline before they ever open the modal. Holding Phase 4
 * hostage to a third referral that may never come would punish the people
 * doing the most.
 */
export const REFERRALS_TARGET = 3;
export const REFERRALS_TO_COMPLETE = 1;

export const ALL_GETTING_STARTED_TASKS = GETTING_STARTED_PHASES.flatMap((p) => p.tasks);

/** The phase a step belongs to. */
export const phaseOfTask = (key: GettingStartedTaskKey): GettingStartedPhase =>
  GETTING_STARTED_PHASES.find((p) => p.tasks.some((t) => t.key === key)) ??
  GETTING_STARTED_PHASES[0];

/**
 * Where a step's work actually happens — keyed by route pathname.
 *
 * The sidebar dock uses this to ask "Finished?" on the page a step lives on,
 * even when the user navigated there by hand rather than through the step's
 * CTA. Order matters: the first unfinished, unlocked step wins, so /profile
 * asks about the profile step before the S.T.A.R. one.
 *
 * Pathnames only, no query strings — `/directory?preview=me` and `/directory`
 * are the same page as far as "which step lives here" is concerned.
 *
 * /programs is deliberately absent. All three Launch steps live there, so a
 * visit by hand would always ask about the first unfinished one whatever the
 * user came to do. Their CTAs set the return step instead, so the dock asks
 * about exactly the step that sent the user there.
 */
export const STEP_PAGES: Record<string, GettingStartedTaskKey[]> = {
  "/profile": ["xo.activate.profile", "xo.activate.stars"],
  "/events": ["xo.activate.kickoff"],
  "/directory": ["xo.publish.live", "xo.connect.peer"],
};

/**
 * Every key the server will accept — mirrors GETTING_STARTED_TASK_KEYS. The
 * `mandatory` onboarding steps are left out: their state lives on the user
 * record, and the server would reject their keys.
 */
export const ALL_TASK_KEYS: GettingStartedTaskKey[] = ALL_GETTING_STARTED_TASKS.filter(
  (t) => !t.mandatory,
).map((t) => t.key);

/**
 * The keys that are actually stored.
 *
 * A derived step computes its own tick from real data, so it has nothing to
 * persist. Everything else is an acknowledgement and needs a row.
 */
export const ACKNOWLEDGED_TASK_KEYS: GettingStartedTaskKey[] = ALL_GETTING_STARTED_TASKS.filter(
  (t) => t.source === "acknowledged",
).map((t) => t.key);

/** Total across the whole journey — the denominator in "x of 15 steps done". */
export const TOTAL_STEP_COUNT = ALL_GETTING_STARTED_TASKS.length;

/** Rough total for the welcome card, summed from the per-step estimates. */
export const TOTAL_ESTIMATE_LABEL = "~2 hr";
